import React, { useState, useEffect } from "react";
import { Car, Clock, FileText, DollarSign, Send, Star } from "lucide-react";
import { useFormState } from "../../hooks/useFormState";
import { formatTime } from "../../utils/formatters";
import { API_BASE_URL } from "../../utils/constants";
import { ImageModal } from "./Modals";
import Creatable from "react-select/creatable";
import PlacaDisplay from "./PlacaDisplay";

const FormularioCompleto = ({
  caseData,
  timeLeft,
  isExpired,
  onSubmit,
  displayError,
}) => {
  const {
    formInput,
    situacaoOptions,
    handleChange,
    handleSituacaoChange,
    handleCreateSituacao,
    handleAvaliacaoChange,
    isFormValid,
  } = useFormState(displayError, caseData);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    if (!showImageModal) return;
    const onKeyDown = (e) => e.key === "Escape" && setShowImageModal(false);
    document.addEventListener("keydown", onKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [showImageModal]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFormValid()) {
      onSubmit(formInput);
    } else {
      onSubmit(null);
    }
  };

  // Atualizado: Verifica se tem a URL da AWS ou o ID do GridFS
  const hasImage = !!caseData?.imagem_url || !!caseData?.imagem_veiculo;

  // Define a fonte da imagem dinamicamente (AWS com prioridade sobre o GridFS)
  const imageSource = caseData?.imagem_url
    ? caseData.imagem_url
    : `${API_BASE_URL}/api/v1/caso/imagem/${caseData?.imagem_veiculo}`;

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 w-full animate-fade-in">
        <div
          className={`lg:col-span-2 flex flex-col items-center gap-4 ${hasImage ? "justify-start" : "justify-center"
            }`}
        >
          {hasImage && (
            <button
              type="button"
              onClick={() => setShowImageModal(true)}
              className="focus:outline-none"
            >
              <img
                src={imageSource}
                alt="Imagem do veículo"
                className="rounded-lg shadow-lg w-full h-auto object-cover hover:opacity-90 transition"
              />
            </button>
          )}
          <PlacaDisplay placa={caseData?.placa} clicavel={true} />
        </div>

        <div className="lg:col-span-3">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {isExpired && (
              <div className="text-center mb-4 rounded-md bg-yellow-400 p-4 text-black font-semibold shadow">
                ⚠️ O seu tempo de resposta está muito longo. Respostas rápidas
                motivam os localizadores a trazerem mais resultados.
              </div>
            )}

            <div className="bg-white border-l-4 border-red-600 rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2 sm:gap-4 mb-4 border-b pb-2">
                <div className="flex items-center gap-2">
                  <Car className="text-red-600" size={20} />
                  <h2 className="text-base sm:text-lg font-semibold text-gray-800">
                    Dados do Veículo
                  </h2>
                </div>
                <div
                  className={`flex items-center gap-2 sm:gap-3 font-extrabold text-2xl px-5
                    sm:px-5 py-2 rounded-lg shadow-md transition-colors duration-300 
                    ${isExpired // Se expirou, o fundo é apenas vermelho sólido para indicar final
                      ? "bg-red-700 text-white"
                      : timeLeft < 60 // Se o tempo for crítico (menos de 60 segundos)
                        ? "bg-red-500 text-white blinking-background" // Vermelho mais vibrante e piscante
                        : "bg-green-600 text-white" // Se há tempo suficiente, fundo verde
                    }
                  `}
                >
                  <Clock size={24} className="sm:w-7 sm:h-7" />
                  <span>{formatTime(timeLeft)}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="dados_bem"
                    className="font-medium text-gray-700"
                  >
                    Descrição <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="dados_bem"
                    name="dados_bem"
                    value={formInput.dados_bem}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2"
                    placeholder="Ex: Corolla XEI 2020, branco"
                  />
                </div>
                <div>
                  <label
                    htmlFor="situacao"
                    className="font-medium text-gray-700"
                  >
                    Situação <span className="text-red-500">*</span>
                  </label>
                  <Creatable
                    id="situacao"
                    isClearable
                    options={situacaoOptions}
                    value={formInput.situacao}
                    onChange={handleSituacaoChange}
                    onCreateOption={handleCreateSituacao}
                    placeholder="Selecione ou crie..."
                    formatCreateLabel={(inputValue) =>
                      `Pressione enter para criar: ${inputValue}`
                    }
                  />
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-yellow-400 rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-4 border-b pb-2">
                <FileText className="text-yellow-400" size={20} />
                <h2 className="text-lg font-semibold text-gray-800">
                  Dados da Ficha
                </h2>
              </div>
              <label
                htmlFor="dados_ficha"
                className="font-medium text-gray-700"
              >
                Informe o Detalhamento da Ficha{" "}
              </label>
              <textarea
                id="dados_ficha"
                name="dados_ficha"
                value={formInput.dados_ficha}
                onChange={handleChange}
                rows="4"
                className="w-full border border-gray-300 rounded-lg p-2"
                placeholder="Cole aqui os dados da ficha"
              />
            </div>

            <div className="bg-white border-l-4 border-green-500 rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-4 border-b pb-2">
                <DollarSign className="text-green-500" size={20} />
                <h2 className="text-lg font-semibold text-gray-800">
                  Recompensa
                </h2>
              </div>
              <label htmlFor="recompensa" className="font-medium text-gray-700">
                Informações de Recompensa
              </label>
              <div className="relative mt-1">
                <DollarSign
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  id="recompensa"
                  name="recompensa"
                  value={formInput.recompensa}
                  onChange={handleChange}
                  className="pl-10 w-full border border-gray-300 rounded-lg p-2"
                  placeholder="Ex: R$3000,00 + R$500,00 de guincho"
                />
              </div>
            </div>

            <div className="bg-white border-l-4 border-purple-500 rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-4 border-b pb-2">
                <Star className="text-purple-500" size={20} />
                <h2 className="text-lg font-semibold text-gray-800">
                  Avaliar localizador
                </h2>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Nota de 1 a 5 em cada critério. A avaliação é interna e auxilia no controle de qualidade.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: "placaCorreta", label: "Placa correta (30%)" },
                  { key: "localizacao", label: "Localização útil (25%)" },
                  { key: "imagem", label: "Imagem / prova (20%)" },
                  { key: "agilidade", label: "Agilidade (15%)" },
                  { key: "conduta", label: "Conduta (10%)" },
                ].map((item) => (
                  <div key={item.key}>
                    <label className="font-medium text-gray-700 text-sm">
                      {item.label}
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg p-2 mt-1"
                      value={formInput.avaliacao?.[item.key] ?? 5}
                      onChange={(e) =>
                        handleAvaliacaoChange(item.key, e.target.value)
                      }
                    >
                      {[5, 4, 3, 2, 1].map((n) => (
                        <option key={n} value={n}>
                          {n} estrela{n > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <label
                htmlFor="comentarioAvaliacao"
                className="font-medium text-gray-700 mt-4 block"
              >
                Comentário (opcional)
              </label>
              <textarea
                id="comentarioAvaliacao"
                name="comentarioAvaliacao"
                value={formInput.comentarioAvaliacao || ""}
                onChange={handleChange}
                rows="2"
                className="w-full border border-gray-300 rounded-lg p-2 mt-1"
                placeholder="Observações sobre a ocorrência"
              />
            </div>

            <div className="flex justify-center mt-4 sm:mt-6">
              <button
                type="submit"
                className="group flex items-center justify-center gap-3 font-bold text-base sm:text-lg py-3 w-full max-w-xs mx-auto sm:w-auto sm:px-8 rounded-lg shadow-lg bg-gradient-to-r from-green-500 to-green-600 text-white hover:scale-105 transition duration-200"
              >
                <Send size={20} />
                <span>Enviar Dados</span>
              </button>
            </div>
          </form>
        </div>
      </div>
      {showImageModal && (
        <ImageModal
          imageUrl={imageSource}
          onClose={() => setShowImageModal(false)}
        />
      )}
    </>
  );
};

export default FormularioCompleto;